import { Panel } from "primereact/panel";
import { formatDateTime } from "@/lib/utils";
import type { AdminParticipantDetail } from "@/lib/admin-api/participants";

type ParticipantWithUserRelations = AdminParticipantDetail & {
  approvedByUser?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string | null;
  } | null;
  rejectedByUser?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string | null;
  } | null;
  cancelledByUser?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string | null;
  } | null;
  balanceTakenByUser?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string | null;
  } | null;
};

type TimelineEvent = {
  date: string;
  type: "created" | "approved" | "rejected" | "cancelled" | "balance_taken";
  title: string;
  description: string;
  user?: {
    firstName: string;
    lastName: string;
    email: string | null;
  } | null;
};

function formatUserName(user: { firstName: string; lastName: string }): string {
  return `${user.firstName} ${user.lastName}`;
}

export function ParticipantTimeline({
  participant,
}: {
  participant: AdminParticipantDetail | null;
}) {
  if (!participant) return null;

  // Type assertion to access user relations that are included in API response
  // but not in generated types
  const participantWithUsers = participant as ParticipantWithUserRelations;

  const events: TimelineEvent[] = [];

  // Created
  if (participantWithUsers.createdAt) {
    events.push({
      date: participantWithUsers.createdAt,
      type: "created",
      title: "Booking Created",
      description: "Participant booking was created",
    });
  }

  // Approved
  if (participantWithUsers.approvedAt) {
    const user = participantWithUsers.approvedByUser;
    events.push({
      date: participantWithUsers.approvedAt,
      type: "approved",
      title: "Approved",
      description: user
        ? `Approved by ${formatUserName(user)}`
        : "Approved by system",
      user: user ?? undefined,
    });
  }

  // Rejected
  if (participantWithUsers.rejectedAt) {
    const user = participantWithUsers.rejectedByUser;
    events.push({
      date: participantWithUsers.rejectedAt,
      type: "rejected",
      title: "Rejected",
      description: user
        ? `Rejected by ${formatUserName(user)}`
        : "Rejected by system",
      user: user ?? undefined,
    });
    if (participantWithUsers.rejectionReason) {
      events[events.length - 1].description += `: ${participantWithUsers.rejectionReason}`;
    }
  }

  // Cancelled
  if (participantWithUsers.cancelledAt) {
    const user = participantWithUsers.cancelledByUser;
    events.push({
      date: participantWithUsers.cancelledAt,
      type: "cancelled",
      title: "Cancelled",
      description: user
        ? `Cancelled by ${formatUserName(user)}`
        : "Cancelled by system",
      user: user ?? undefined,
    });
    if (participantWithUsers.cancellationReason) {
      events[events.length - 1].description += `: ${participantWithUsers.cancellationReason}`;
    }
  }

  // Balance Taken
  if (participantWithUsers.balanceTakenAt) {
    const user = participantWithUsers.balanceTakenByUser;
    events.push({
      date: participantWithUsers.balanceTakenAt,
      type: "balance_taken",
      title: "Balance Payment Taken",
      description: user
        ? `Balance payment taken by ${formatUserName(user)}`
        : "Balance payment taken by system",
      user: user ?? undefined,
    });
  }

  // Sort events by date (most recent first)
  events.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  if (events.length === 0) return null;

  const getEventIcon = (type: TimelineEvent["type"]) => {
    switch (type) {
      case "created":
        return "pi pi-calendar-plus";
      case "approved":
        return "pi pi-check-circle";
      case "rejected":
        return "pi pi-times-circle";
      case "cancelled":
        return "pi pi-ban";
      case "balance_taken":
        return "pi pi-credit-card";
    }
  };

  const getEventColor = (type: TimelineEvent["type"]) => {
    switch (type) {
      case "created":
        return "text-blue-600";
      case "approved":
        return "text-green-600";
      case "rejected":
        return "text-red-600";
      case "cancelled":
        return "text-orange-600";
      case "balance_taken":
        return "text-purple-600";
    }
  };

  return (
    <Panel header="Timeline" className="mb-3">
      <div className="flex flex-column gap-3">
        {events.map((event, index) => (
          <div key={`${event.type}-${event.date}`} className="flex gap-3">
            <div className="flex flex-column align-items-center">
              <i
                className={`${getEventIcon(event.type)} ${getEventColor(
                  event.type,
                )} text-xl`}
              />
              {index < events.length - 1 && (
                <div className="w-px bg-gray-300 flex-1 mt-2" style={{ minHeight: "2rem" }} />
              )}
            </div>
            <div className="flex-1">
              <div className="font-semibold">{event.title}</div>
              <div className="text-sm text-gray-600">{event.description}</div>
              <div className="text-xs text-gray-500 mt-1">
                {formatDateTime(event.date)}
              </div>
            </div>
          </div>
        ))}
      </div>
    </Panel>
  );
}

